import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Card,
  Stack,
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  Box,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { collection, getDocs, getFirestore, deleteDoc, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import { UserListHead } from '../sections/@dashboard/user';
import firebaseApp from '../utils/firebaseConfig';

const db = getFirestore(firebaseApp);

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const RULES_TABLE_HEAD = [
  { id: 'dayOfWeek', label: 'Day', alignRight: false },
  { id: 'startTime', label: 'Start', alignRight: false },
  { id: 'endTime', label: 'End', alignRight: false },
  { id: 'categoryId', label: 'Category', alignRight: false },
  { id: 'slotDurationMinutes', label: 'Slot Duration', alignRight: false },
  { id: 'maxConcurrentBookings', label: 'Max Bookings', alignRight: false },
  { id: 'active', label: 'Active', alignRight: false },
  { id: '', label: '', alignRight: false },
];

const BLOCKS_TABLE_HEAD = [
  { id: 'date', label: 'Date', alignRight: false },
  { id: 'categoryId', label: 'Category', alignRight: false },
  { id: 'laneId', label: 'Lane', alignRight: false },
  { id: 'startTime', label: 'Start', alignRight: false },
  { id: 'endTime', label: 'End', alignRight: false },
  { id: 'reason', label: 'Reason', alignRight: false },
  { id: '', label: '', alignRight: false },
];

const emptyRule = {
  dayOfWeek: 1,
  startTime: '09:00',
  endTime: '17:00',
  categoryIds: [],
  slotDurationMinutes: 60,
  maxConcurrentBookings: 1,
  active: true,
};

const emptyBlock = {
  date: '',
  endDate: '',
  dateMode: 'single', // 'single' | 'range'
  categoryId: '',
  laneId: '',
  startTime: '',
  endTime: '',
  reason: '',
};

function datesInRange(startISO, endISO) {
  const out = [];
  if (!startISO || !endISO) return out;
  const start = new Date(`${startISO}T00:00:00`);
  const end = new Date(`${endISO}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return out;
  if (end < start) return out;
  const cur = new Date(start);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function AvailabilityPage() {
  const [rules, setRules] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lanes, setLanes] = useState([]);

  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState(emptyRule);
  const [selectedDays, setSelectedDays] = useState([1]); // multi-day select para create mode
  const [blockForm, setBlockForm] = useState(emptyBlock);
  const [filterCategory, setFilterCategory] = useState(''); // 'all' = '', or '__none__' for null categoryId
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setTableLoading(true);
    try {
      const [rulesSnap, blocksSnap, catsSnap, lanesSnap] = await Promise.all([
        getDocs(collection(db, 'availability_rules')),
        getDocs(collection(db, 'blocked_dates')),
        getDocs(collection(db, 'reservation_categories')),
        getDocs(collection(db, 'lanes')),
      ]);
      setRules(rulesSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
      setBlocks(blocksSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
      setCategories(catsSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
      setLanes(lanesSnap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setTableLoading(false);
  };

  const getCategoryName = (catId) => {
    if (!catId) return 'All';
    const cat = categories.find((c) => c.id === catId);
    return cat ? cat.name : catId;
  };

  const getLaneName = (laneId) => {
    if (!laneId) return 'All';
    const lane = lanes.find((l) => l.id === laneId);
    return lane ? lane.name : laneId;
  };

  // --- Rules ---
  const openRuleDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule.id);
      const catIds = rule.categoryId ? [rule.categoryId] : [];
      setRuleForm({ ...emptyRule, ...rule, categoryIds: catIds });
      setSelectedDays([Number(rule.dayOfWeek)]);
    } else {
      setEditingRule(null);
      setRuleForm(emptyRule);
      setSelectedDays([1]);
    }
    setRuleDialogOpen(true);
  };

  const saveRule = async () => {
    try {
      if (!selectedDays.length) {
        alert('Selecciona al menos un día.');
        return;
      }
      const { categoryIds, ...rest } = ruleForm;
      const catList = categoryIds.length > 0 ? categoryIds : [null];

      const baseData = {
        ...rest,
        slotDurationMinutes: Number(rest.slotDurationMinutes),
        maxConcurrentBookings: Number(rest.maxConcurrentBookings),
        updatedAt: serverTimestamp(),
      };

      if (editingRule) {
        if (selectedDays.length === 1 && catList.length === 1) {
          await updateDoc(doc(db, 'availability_rules', editingRule), {
            ...baseData,
            categoryId: catList[0],
            dayOfWeek: Number(selectedDays[0]),
          });
        } else {
          await deleteDoc(doc(db, 'availability_rules', editingRule));
          await Promise.all(
            selectedDays.flatMap((dow) =>
              catList.map((catId) =>
                setDoc(doc(collection(db, 'availability_rules')), {
                  ...baseData,
                  categoryId: catId,
                  dayOfWeek: Number(dow),
                  createdAt: serverTimestamp(),
                })
              )
            )
          );
        }
      } else {
        await Promise.all(
          selectedDays.flatMap((dow) =>
            catList.map((catId) =>
              setDoc(doc(collection(db, 'availability_rules')), {
                ...baseData,
                categoryId: catId,
                dayOfWeek: Number(dow),
                createdAt: serverTimestamp(),
              })
            )
          )
        );
      }
      setRuleDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error('Error saving rule:', error);
      alert('Error saving rule');
    }
  };

  const handleDaysChange = (event, newDays) => {
    setSelectedDays(newDays || []);
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    await deleteDoc(doc(db, 'availability_rules', id));
    setRules(rules.filter((r) => r.id !== id));
  };

  const toggleRuleActive = async (rule) => {
    await updateDoc(doc(db, 'availability_rules', rule.id), { active: !rule.active });
    setRules(rules.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
  };

  // --- Blocked Dates ---
  const openBlockDialog = () => {
    setBlockForm(emptyBlock);
    setBlockDialogOpen(true);
  };

  const saveBlock = async () => {
    if (!blockForm.date) { alert('Date is required'); return; }
    if (blockForm.dateMode === 'range' && !blockForm.endDate) {
      alert('End date is required for range mode');
      return;
    }
    try {
      const baseData = {
        categoryId: blockForm.categoryId || null,
        laneId: blockForm.laneId || null,
        startTime: blockForm.startTime || null,
        endTime: blockForm.endTime || null,
        reason: blockForm.reason || '',
        createdAt: serverTimestamp(),
      };

      const dates =
        blockForm.dateMode === 'range'
          ? datesInRange(blockForm.date, blockForm.endDate)
          : [blockForm.date];

      if (dates.length === 0) {
        alert('Rango de fechas inválido');
        return;
      }

      await Promise.all(
        dates.map((d) =>
          setDoc(doc(collection(db, 'blocked_dates')), { ...baseData, date: d })
        )
      );
      setBlockDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error('Error saving block:', error);
      alert('Error saving blocked date');
    }
  };

  const deleteBlock = async (id) => {
    if (!window.confirm('Delete this blocked date?')) return;
    await deleteDoc(doc(db, 'blocked_dates', id));
    setBlocks(blocks.filter((b) => b.id !== id));
  };

  const matchesCategory = (catId) => {
    if (!filterCategory) return true;
    if (filterCategory === '__none__') return !catId;
    return catId === filterCategory;
  };

  const sortedRules = [...rules]
    .filter((r) => matchesCategory(r.categoryId))
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime));
  const sortedBlocks = [...blocks]
    .filter((b) => matchesCategory(b.categoryId))
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <>
      <Helmet>
        <title>Availability | BBTC Dashboard</title>
      </Helmet>

      <Container>
        {/* Filtro global por categoría */}
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Filtrar por categoría:
          </Typography>
          <TextField
            select
            size="small"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Todas las categorías</MenuItem>
            <MenuItem value="__none__"><em>Sin categoría (todas)</em></MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </TextField>
          {filterCategory && (
            <Chip
              label={`Filtrando: ${filterCategory === '__none__' ? 'Sin categoría' : getCategoryName(filterCategory)}`}
              onDelete={() => setFilterCategory('')}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Stack>

        {/* --- Availability Rules --- */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Operating Hours</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => openRuleDialog()}>
            Add Rule
          </Button>
        </Stack>

        <Card sx={{ mb: 5 }}>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={RULES_TABLE_HEAD} />
                <TableBody>
                  {tableLoading && <TableSkeleton rows={3} columns={7} />}
                  {!tableLoading && sortedRules.map((rule) => (
                    <TableRow hover key={rule.id}>
                      <TableCell padding="checkbox" />
                      <TableCell><Chip label={DAYS_OF_WEEK[rule.dayOfWeek]} size="small" /></TableCell>
                      <TableCell>{rule.startTime}</TableCell>
                      <TableCell>{rule.endTime}</TableCell>
                      <TableCell>{getCategoryName(rule.categoryId)}</TableCell>
                      <TableCell>{rule.slotDurationMinutes} min</TableCell>
                      <TableCell>{rule.maxConcurrentBookings}</TableCell>
                      <TableCell><Switch checked={rule.active} onChange={() => toggleRuleActive(rule)} size="small" /></TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openRuleDialog(rule)}><Iconify icon="eva:edit-fill" /></IconButton>
                        <IconButton onClick={() => deleteRule(rule.id)} color="error"><Iconify icon="eva:trash-2-outline" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedRules.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}><Typography variant="body2">No rules configured</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>

        {/* --- Blocked Dates --- */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Blocked Dates</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openBlockDialog}>
            Add Block
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={BLOCKS_TABLE_HEAD} />
                <TableBody>
                  {tableLoading && <TableSkeleton rows={3} columns={6} />}
                  {!tableLoading && sortedBlocks.map((block) => (
                    <TableRow hover key={block.id}>
                      <TableCell padding="checkbox" />
                      <TableCell>{block.date}</TableCell>
                      <TableCell>{getCategoryName(block.categoryId)}</TableCell>
                      <TableCell>{getLaneName(block.laneId)}</TableCell>
                      <TableCell>{block.startTime || 'All Day'}</TableCell>
                      <TableCell>{block.endTime || '—'}</TableCell>
                      <TableCell>{block.reason}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => deleteBlock(block.id)} color="error"><Iconify icon="eva:trash-2-outline" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedBlocks.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><Typography variant="body2">No blocked dates</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>

      {/* --- Rule Dialog --- */}
      <Dialog open={ruleDialogOpen} onClose={() => setRuleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Días de la semana (selecciona varios)</Typography>
                <ButtonGroup size="small" variant="outlined">
                  <Button onClick={() => setSelectedDays([0, 1, 2, 3, 4, 5, 6])}>Todos</Button>
                  <Button onClick={() => setSelectedDays([1, 2, 3, 4, 5])}>Lun–Vie</Button>
                  <Button onClick={() => setSelectedDays([0, 6])}>Sáb–Dom</Button>
                  <Button onClick={() => setSelectedDays([])}>Limpiar</Button>
                </ButtonGroup>
              </Stack>
              <ToggleButtonGroup
                value={selectedDays}
                onChange={handleDaysChange}
                aria-label="días de la semana"
                size="small"
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {DAYS_SHORT.map((d, i) => (
                  <ToggleButton
                    key={i}
                    value={i}
                    sx={{
                      minWidth: 56,
                      borderRadius: '8px !important',
                      border: '1px solid rgba(0,0,0,0.12) !important',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                    }}
                  >
                    {d}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Si seleccionas más de un día o categoría, se creará una regla por cada combinación.
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Time" type="time" value={ruleForm.startTime} onChange={(e) => setRuleForm({ ...ruleForm, startTime: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Time" type="time" value={ruleForm.endTime} onChange={(e) => setRuleForm({ ...ruleForm, endTime: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={categories}
                value={categories.filter((c) => ruleForm.categoryIds.includes(c.id))}
                onChange={(_, newVal) => setRuleForm({ ...ruleForm, categoryIds: newVal.map((c) => c.id) })}
                getOptionLabel={(opt) => opt.name}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} size="small" />
                    {option.name}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Categorías (opcional)" placeholder={ruleForm.categoryIds.length === 0 ? 'Todas las categorías' : ''} />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Slot Duration (min)" value={ruleForm.slotDurationMinutes} onChange={(e) => setRuleForm({ ...ruleForm, slotDurationMinutes: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Max Concurrent Bookings" value={ruleForm.maxConcurrentBookings} onChange={(e) => setRuleForm({ ...ruleForm, maxConcurrentBookings: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={ruleForm.active} onChange={(e) => setRuleForm({ ...ruleForm, active: e.target.checked })} />} label="Active" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRuleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRule}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* --- Block Dialog --- */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Blocked Date</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={blockForm.dateMode}
                onChange={(e, v) => v && setBlockForm({ ...blockForm, dateMode: v })}
                sx={{ mb: 1 }}
              >
                <ToggleButton value="single">Día único</ToggleButton>
                <ToggleButton value="range">Rango de fechas</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={blockForm.dateMode === 'range' ? 6 : 12}>
              <TextField
                fullWidth
                label={blockForm.dateMode === 'range' ? 'Desde' : 'Date'}
                type="date"
                value={blockForm.date}
                onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {blockForm.dateMode === 'range' && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hasta"
                  type="date"
                  value={blockForm.endDate}
                  onChange={(e) => setBlockForm({ ...blockForm, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {blockForm.dateMode === 'range' && blockForm.date && blockForm.endDate && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Se crearán {datesInRange(blockForm.date, blockForm.endDate).length} bloqueos (uno por día).
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth select label="Category (optional)" value={blockForm.categoryId} onChange={(e) => setBlockForm({ ...blockForm, categoryId: e.target.value })}>
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label="Lane (optional)" value={blockForm.laneId} onChange={(e) => setBlockForm({ ...blockForm, laneId: e.target.value })}>
                <MenuItem value="">All Lanes</MenuItem>
                {lanes.map((lane) => <MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Time (optional)" type="time" value={blockForm.startTime} onChange={(e) => setBlockForm({ ...blockForm, startTime: e.target.value })} InputLabelProps={{ shrink: true }} helperText="Leave empty for all day" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Time (optional)" type="time" value={blockForm.endTime} onChange={(e) => setBlockForm({ ...blockForm, endTime: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Reason" value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBlock}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
